import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ---------------------------------------------------------------------------
// Client-side mock for therapist profile extras.
// These fields are part of the redesigned "Ustawienia konta" (see Figma) but
// are not yet supported by the backend. To provide real, working behaviour in
// the UI now — most importantly the search-visibility toggle that drives the
// dashboard card — we persist them in localStorage until the API catches up.
// ---------------------------------------------------------------------------
export interface SessionRate {
    sessionType: string;
    price: string;
    duration: string;
}

export interface TherapistProfile {
    therapyMode: string;            // 'online_office' | 'online' | 'office'
    region: string;
    city: string;
    bio: string;
    birthDate: string;
    gender: string;
    specializations: string[];
    worksWith: string[];            // 'children' | 'youth' | 'adults' | 'couples'
    rates: SessionRate[];
    contactEmail: string;
    contactPhone: string;
    website: string;                // gated behind paid plan
}

export interface ProfileState {
    _id: string;
    externalId: string;
    name: string;
    email: string;
    role: string;
    phone: string;
    companyName: string;
    companyNIP: string;
    companyCountry: string;
    companyAddress: string;
    companyEmail: string;
    agreement: boolean;
    marketingEmails: boolean;
    invoiceToCompany: boolean;
    deleteRequestSubmitted: string;
    // Client-side extras (mocked until backend support lands):
    plan: string;                   // 'free' => "Konto Darmowe"
    position: string;               // Stanowisko
    avatarUrl: string;
    searchVisible: boolean;
    therapist: TherapistProfile;
}

const LS_KEY = 'emdrlink.profileExtras';

const defaultTherapist: TherapistProfile = {
    therapyMode: 'online_office',
    region: '',
    city: '',
    bio: '',
    birthDate: '',
    gender: '',
    specializations: [],
    worksWith: [],
    rates: [],
    contactEmail: '',
    contactPhone: '',
    website: '',
};

interface ProfileExtras {
    plan: string;
    position: string;
    avatarUrl: string;
    searchVisible: boolean;
    therapist: TherapistProfile;
}

const loadExtras = (): ProfileExtras => {
    const base: ProfileExtras = {
        plan: 'free',
        position: '',
        avatarUrl: '',
        searchVisible: false,
        therapist: {...defaultTherapist},
    };
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return base;
        const parsed = JSON.parse(raw);
        return {
            ...base,
            ...parsed,
            therapist: {...defaultTherapist, ...(parsed.therapist || {})},
        };
    } catch {
        return base;
    }
};

const persistExtras = (state: ProfileState) => {
    try {
        const extras: ProfileExtras = {
            plan: state.plan,
            position: state.position,
            avatarUrl: state.avatarUrl,
            searchVisible: state.searchVisible,
            therapist: state.therapist,
        };
        localStorage.setItem(LS_KEY, JSON.stringify(extras));
    } catch {
        /* ignore quota/availability errors */
    }
};

const extras = loadExtras();

const initialState: ProfileState = {
    _id: "",
    externalId: "",
    name: "",
    email: "",
    role: "",
    phone: "",
    companyName: "",
    companyNIP: "",
    companyAddress: "",
    companyCountry: "",
    companyEmail: "",
    agreement: false,
    marketingEmails: false,
    invoiceToCompany: false,
    deleteRequestSubmitted: "",
    ...extras,
};

// setProfileData only carries the server-backed fields.
type ServerProfilePayload = Omit<ProfileState, 'plan' | 'position' | 'avatarUrl' | 'searchVisible' | 'therapist'>;

export const profileSlice = createSlice({
    name: 'profile',
    initialState,
    reducers: {
        setProfileData: (state, action: PayloadAction<ServerProfilePayload>) => {
            state._id = action.payload._id;
            state.externalId = action.payload.externalId;
            state.name = action.payload.name;
            state.email = action.payload.email;
            state.role = action.payload.role;
            state.phone = action.payload.phone;
            state.companyName = action.payload.companyName;
            state.companyNIP = action.payload.companyNIP;
            state.companyAddress = action.payload.companyAddress;
            state.companyCountry = action.payload.companyCountry;
            state.companyEmail = action.payload.companyEmail;
            state.marketingEmails = action.payload.marketingEmails;
            state.agreement = action.payload.agreement;
            state.invoiceToCompany = action.payload.invoiceToCompany;
            state.deleteRequestSubmitted = action.payload.deleteRequestSubmitted;
            // Client-side extras (plan/position/searchVisible/therapist) are left
            // untouched so a profile refresh doesn't wipe the mocked settings.
        },
        setSearchVisibility: (state, action: PayloadAction<boolean>) => {
            state.searchVisible = action.payload;
            persistExtras(state);
        },
        setPosition: (state, action: PayloadAction<string>) => {
            state.position = action.payload;
            persistExtras(state);
        },
        setAvatarUrl: (state, action: PayloadAction<string>) => {
            state.avatarUrl = action.payload;
            persistExtras(state);
        },
        updateTherapistProfile: (state, action: PayloadAction<Partial<TherapistProfile>>) => {
            state.therapist = {...state.therapist, ...action.payload};
            persistExtras(state);
        },
        deleteProfile: (state) => {
            state.deleteRequestSubmitted = new Date().toString();
        },
        cancelDeleteRequest: (state) => {
            state.deleteRequestSubmitted = "";
        }
    }
});
export const {
    setProfileData,
    setSearchVisibility,
    setPosition,
    setAvatarUrl,
    updateTherapistProfile,
    deleteProfile,
    cancelDeleteRequest,
} = profileSlice.actions;

export default profileSlice.reducer;
